import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PERSON_ROLES,
  type PersonRequest,
  useApplyConversationValues,
  useCreateConversation,
  useUploadConversationAudio,
} from '@/entities/conversations';
import { getApiErrorMessage } from '@/shared/api';
import { toastError, toastSuccess } from '@/shared/lib';
import { RECORD_MAX_FILES } from './constants';
import { getRecordFileKey } from './recordFiles';
import type { RecordValues } from './recordValueSet';

type Params = {
  /** Отмеченные записи — им проставляются значения набора */
  pickedIds: number[];
  /** Снятие отметок после проставления значений */
  onApplied: () => void;
  /** Перезапрос показанных строк списка: добавленные и изменённые записи грид сам не подтягивает */
  onRecordsChanged: () => void;
};

/**
 * Участники создаваемой записи по набору значений. Клиента без идентификатора бэк заводит новым
 * участником, поэтому у каждой записи свой клиент с введёнными частями имени.
 */
const buildRecordPersons = ({ employeeId, client }: RecordValues): PersonRequest[] => {
  const persons: PersonRequest[] = [];
  if (employeeId !== undefined) persons.push({ id: employeeId });
  if (client !== undefined) persons.push({ role: PERSON_ROLES.CLIENT, ...client });
  return persons;
};

/**
 * Действия блока загрузки: добавление записей по файлам порции и проставление значений набора
 * отмеченным записям, вместе с самой порцией файлов и признаком идущей отправки.
 */
export const useRecordsUpload = ({ pickedIds, onApplied, onRecordsChanged }: Params) => {
  const { t } = useTranslation(['records']);
  const [files, setFiles] = useState<File[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const { mutateAsync: uploadAudio } = useUploadConversationAudio();
  const { mutateAsync: createConversation } = useCreateConversation();
  const { mutate: applyValues, isPending: isApplying } = useApplyConversationValues();

  // Область загрузки называет в сообщении и отклонённый файл, и причину
  const reportRejectedFiles = useCallback((errors: string[]) => {
    for (const error of errors) toastError(error);
  }, []);

  // Предел порции проверяется здесь, а не областью загрузки: только тут известно, какие из выбранных
  // файлов для порции новые — повторно выбранный файл места в ней не занимает
  const addFiles = useCallback(
    (accepted: File[]) => {
      const addedKeys = new Set(files.map(getRecordFileKey));
      const newFiles = accepted.filter((file) => !addedKeys.has(getRecordFileKey(file)));
      const freeSlots = RECORD_MAX_FILES - files.length;
      const limitError = t('records:fileUpload.maxFilesExceeded', { maxFiles: RECORD_MAX_FILES });
      reportRejectedFiles(newFiles.slice(freeSlots).map((file) => `${file.name}: ${limitError}`));
      setFiles([...files, ...newFiles.slice(0, freeSlots)]);
    },
    [files, reportRejectedFiles, t],
  );

  const removeFile = useCallback((fileKey: string) => {
    setFiles((prev) => prev.filter((file) => getRecordFileKey(file) !== fileKey));
  }, []);

  const addRecords = useCallback(
    async (values: RecordValues) => {
      setIsAdding(true);
      let addedCount = 0;
      const person = buildRecordPersons(values);

      // Файлы загружаются по очереди: отказ по одному не останавливает остальные
      for (const file of files) {
        try {
          const { fileName } = await uploadAudio(file);
          const callTime = values.startedAt ?? new Date().toISOString();
          await createConversation({
            startedAt: callTime,
            finishedAt: callTime,
            fileName,
            person,
            attributes: values.attributes,
          });
          addedCount += 1;
        } catch (error) {
          toastError(t('records:upload.fileFailed', { fileName: file.name }), {
            description: getApiErrorMessage(error),
          });
        }
      }

      setFiles([]);
      setIsAdding(false);
      if (addedCount > 0) {
        toastSuccess(t('records:upload.added', { count: addedCount }));
        onRecordsChanged();
      }
    },
    [files, uploadAudio, createConversation, onRecordsChanged, t],
  );

  const applyToRecords = useCallback(
    (values: RecordValues) => {
      applyValues(
        {
          conversationIds: pickedIds,
          employeeId: values.employeeId,
          client: values.client,
          startedAt: values.startedAt,
          attributes: values.attributes.length > 0 ? values.attributes : undefined,
        },
        {
          onSuccess: ({ updated }) => {
            toastSuccess(t('records:upload.applied', { count: updated }));
            onApplied();
            onRecordsChanged();
          },
          onError: (error) => {
            toastError(t('records:upload.applyFailed'), { description: getApiErrorMessage(error) });
          },
        },
      );
    },
    [applyValues, pickedIds, onApplied, onRecordsChanged, t],
  );

  return {
    files,
    addFiles,
    removeFile,
    reportRejectedFiles,
    addRecords,
    applyToRecords,
    isSubmitting: isAdding || isApplying,
  };
};
